import React from 'react';

interface RosterPreviewPanelProps {
    shifts: any[];
    users: any[];
    startDate: string;
    endDate: string;
    onEditShift: (shift: any) => void;
    onApprove: () => void;
    onBack: () => void;
    violations?: string[]; // Constraint violations
    warnings?: string[]; // Warnings
}

const RosterPreviewPanel: React.FC<RosterPreviewPanelProps> = ({
    shifts,
    users,
    startDate,
    endDate,
    onEditShift,
    onApprove,
    onBack,
    violations = [],
    warnings = []
}) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const days: Date[] = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        days.push(new Date(d));
    }

    // Helper to get shift for user/date
    const getShift = (userId: string, date: Date) => {
        return shifts.find(s =>
            s.userId === userId &&
            new Date(s.startTime).toDateString() === date.toDateString()
        );
    };

    // Group users by Role for visual separation
    const groupedUsers = {
        'CONTROLLERS': users.filter(u => u.role?.name === 'CONTROLLER'),
        'HEATHROW CLEARERS': users.filter(u => u.role?.name === 'CLEARER'),
        'WAREHOUSE': users.filter(u => u.role?.name?.toUpperCase() === 'WAREHOUSE'),
        'OFFICES': users.filter(u => ['OFFICE', 'ADMIN'].includes(u.role?.name?.toUpperCase())),
    };

    // Check if shift has violations
    const hasViolation = (shift: any) => {
        if (!shift) return false;
        // Check if shift ID is mentioned in violations
        return violations.some(v => v.includes(shift.id) || v.includes(shift.userId));
    };

    // Color mapping based on content/type
    const getCellClass = (shift: any) => {
        if (!shift) return 'bg-white'; // Empty
        const type = shift.type || shift.shiftType;
        const notes = shift.notes || '';

        // Highlight violations
        if (hasViolation(shift)) {
            return 'bg-red-200 border-2 border-red-500 text-black'; // Violation highlight
        }

        // Check specific codes first
        if (notes === 'Holiday' || type === 'Holiday') return 'bg-red-500 text-white font-bold';
        if (type === 'LHR' || (type === 'CLEARER' && shift.location === 'Heathrow')) {
            return 'bg-green-400 text-black'; // Heathrow clearing
        }
        if (type === 'CLEARER' || notes === 'Clearer') {
            return 'bg-green-300 text-black'; // LGW
        }
        if (type === 'CONTROL' || notes.includes('Control')) return 'bg-orange-400 text-black'; // Controller
        if (type === 'OFFICE' || notes === 'OFFICE') return 'bg-blue-300 text-black'; // Office
        if (type === 'WAREHOUSE') return 'bg-yellow-400 text-black'; // Warehouse

        // Default clearing
        if (type === 'CLEARER' || notes === 'C') return 'bg-green-200 text-black';

        return 'bg-gray-100';
    };

    const getCellContent = (shift: any) => {
        if (!shift) return '';
        if (shift.notes) return shift.notes; // Explicit code like "C", "LGW"
        // Map type to display code
        if (shift.type === 'LHR') return 'LHR';
        if (shift.type === 'CLEARER') return 'Clearer';
        if (shift.type === 'CONTROL') return 'Control';
        if (shift.type === 'OFFICE') return 'OFFICE';
        return shift.type?.charAt(0) || '';
    };

    return (
        <div className="flex flex-col h-full bg-white rounded-lg shadow overflow-hidden">
            {/* Header Controls */}
            <div className="p-4 border-b bg-gray-50">
                <div className="flex justify-between items-center mb-2">
                    <h3 className="font-bold text-lg">Roster Preview</h3>
                    <div className="flex gap-2">
                        <button onClick={onBack} className="px-4 py-2 border rounded hover:bg-gray-100">Back</button>
                        <button
                            onClick={onApprove}
                            className={`px-4 py-2 rounded font-bold ${violations.length > 0
                                ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                                }`}
                        >
                            {violations.length > 0 ? 'Save Draft (with Warnings)' : 'Save as Draft'}
                        </button>
                    </div>
                </div>

                {/* Violations and Warnings */}
                {(violations.length > 0 || warnings.length > 0) && (
                    <div className="mt-2 space-y-1">
                        {violations.length > 0 && (
                            <div className="p-2 bg-red-50 text-red-700 rounded text-xs border border-red-200">
                                <strong>Constraint Violations ({violations.length}):</strong>
                                <ul className="list-disc list-inside ml-2 mt-1">
                                    {violations.slice(0, 3).map((v, idx) => (
                                        <li key={idx}>{v}</li>
                                    ))}
                                    {violations.length > 3 && <li>...and {violations.length - 3} more</li>}
                                </ul>
                            </div>
                        )}
                        {warnings.length > 0 && (
                            <div className="p-2 bg-yellow-50 text-yellow-700 rounded text-xs border border-yellow-200">
                                <strong>Warnings ({warnings.length}):</strong>
                                <ul className="list-disc list-inside ml-2 mt-1">
                                    {warnings.slice(0, 3).map((w, idx) => (
                                        <li key={idx}>{w}</li>
                                    ))}
                                    {warnings.length > 3 && <li>...and {warnings.length - 3} more</li>}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Spreadsheet Grid */}
            <div className="flex-1 overflow-auto relative">
                <table className="min-w-full border-collapse">
                    <thead className="bg-yellow-400 text-black sticky top-0 z-10 font-bold uppercase text-xs">
                        <tr>
                            <th className="p-2 border border-yellow-500 min-w-[150px] sticky left-0 z-20 bg-yellow-400">Name</th>
                            {days.map(d => (
                                <th key={d.toISOString()} className="p-1 border border-yellow-500 min-w-[40px] text-center">
                                    <div className="flex flex-col">
                                        <span>{d.toLocaleDateString(undefined, { weekday: 'short' }).toUpperCase()}</span>
                                        <span>{d.getDate()}</span>
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="text-xs">
                        {Object.entries(groupedUsers).map(([groupName, groupDirectUsers]) => (
                            <React.Fragment key={groupName}>
                                {/* Section Header */}
                                {groupDirectUsers.length > 0 && (
                                    <tr className="bg-amber-300 font-bold">
                                        <td className="p-2 border border-gray-300 sticky left-0 bg-amber-300" colSpan={days.length + 1}>
                                            {groupName}
                                        </td>
                                    </tr>
                                )}

                                {groupDirectUsers.map((user: any) => (
                                    <tr key={user.id} className="hover:bg-gray-50">
                                        <td className="p-2 border border-gray-300 font-medium sticky left-0 bg-white z-10 whitespace-nowrap">
                                            {user.name}
                                        </td>
                                        {days.map(d => {
                                            const shift = getShift(user.id, d);
                                            return (
                                                <td
                                                    key={d.toISOString()}
                                                    className={`border border-gray-300 text-center cursor-pointer hover:opacity-80 ${getCellClass(shift)}`}
                                                    onClick={() => onEditShift(shift || { userId: user.id, date: d })}
                                                >
                                                    {getCellContent(shift)}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Legend */}
            <div className="p-2 flex flex-wrap gap-4 text-xs bg-gray-50 border-t">
                <div className="flex items-center gap-1"><span className="w-4 h-4 bg-green-300 border"></span> LGW</div>
                <div className="flex items-center gap-1"><span className="w-4 h-4 bg-green-400 border"></span> Heathrow</div>
                <div className="flex items-center gap-1"><span className="w-4 h-4 bg-green-200 border"></span> Clearing (C)</div>
                <div className="flex items-center gap-1"><span className="w-4 h-4 bg-orange-400 border"></span> Controller</div>
                <div className="flex items-center gap-1"><span className="w-4 h-4 bg-blue-300 border"></span> Office</div>
                <div className="flex items-center gap-1"><span className="w-4 h-4 bg-red-500 border"></span> Holiday/Off</div>
                {violations.length > 0 && (
                    <div className="flex items-center gap-1"><span className="w-4 h-4 bg-red-200 border-2 border-red-500"></span> Violations</div>
                )}
            </div>
        </div >
    );
};

export default RosterPreviewPanel;
